import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, finalize, of, tap } from 'rxjs';

import { API_V1_URL } from '../../../core/config/api.config';
import { AuthService } from '../../../core/services/auth.service';

export type EntlastungLockSession = {
  readonly lockId: string;
  readonly expiresAt: string;
};

@Injectable({ providedIn: 'root' })
export class EntlastungenLockService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly activeLocks = new Map<string, EntlastungLockSession>();

  acquireLock(filialNummer: number): Observable<EntlastungLockSession> {
    const key = this.lockKey(filialNummer);
    const previous = this.activeLocks.get(key);
    return this.http
      .post<EntlastungLockSession>(`${API_V1_URL}/locks/acquire`, { keys: [key] })
      .pipe(
        tap({
          next: (session) => this.activeLocks.set(key, session),
          error: () => {
            if (this.activeLocks.get(key) === previous) this.activeLocks.delete(key);
          },
        })
      );
  }

  heartbeatLock(filialNummer: number): Observable<EntlastungLockSession | null> {
    const key = this.lockKey(filialNummer);
    const session = this.activeLocks.get(key);
    if (!session) return of(null);

    return this.http
      .put<EntlastungLockSession>(`${API_V1_URL}/locks/${session.lockId}/heartbeat`, null)
      .pipe(
        tap({
          next: (refreshed) => {
            if (this.activeLocks.get(key) === session) this.activeLocks.set(key, refreshed);
          },
          error: (error: unknown) => {
            if (
              this.activeLocks.get(key) === session &&
              error instanceof HttpErrorResponse &&
              (error.status === 404 || error.status === 423)
            ) {
              this.activeLocks.delete(key);
            }
          },
        })
      );
  }

  releaseLock(filialNummer: number): Observable<void> {
    const key = this.lockKey(filialNummer);
    const session = this.activeLocks.get(key);
    if (!session) return of(void 0);

    // Logout releases all session locks in the backend. Once the local token is
    // gone, do not issue a second DELETE for a lock that has already been freed.
    if (!this.authService.getToken()) {
      this.activeLocks.delete(key);
      return of(void 0);
    }

    return this.http.delete<void>(`${API_V1_URL}/locks/${session.lockId}`).pipe(
      finalize(() => {
        if (this.activeLocks.get(key) === session) this.activeLocks.delete(key);
      })
    );
  }

  releaseLockBeforeUnload(filialNummer: number): void {
    const key = this.lockKey(filialNummer);
    const session = this.activeLocks.get(key);
    if (!session) return;

    this.activeLocks.delete(key);
    const token = this.authService.getToken();
    if (!token) return;

    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
    };

    void fetch(`${API_V1_URL}/locks/${session.lockId}`, {
      method: 'DELETE',
      keepalive: true,
      headers,
    }).catch(() => undefined);
  }

  clearLock(filialNummer: number): void {
    this.activeLocks.delete(this.lockKey(filialNummer));
  }

  getLockSession(filialNummer: number): EntlastungLockSession | null {
    return this.activeLocks.get(this.lockKey(filialNummer)) ?? null;
  }

  private lockKey(filialNummer: number): string {
    return `entlastungen:${filialNummer}`;
  }
}
